<script>
import appConfig from "@src/app.config";
import SalesService from "@services/resources/SalesService";
import Actions from "./details/Actions.vue";
import Resume from "./details/Resume.vue";
import Client from "./details/Client.vue";
import SaleParticipations from "./details/SaleParticipations.vue";
import Financial from "./details/Financial.vue";
import Charges from "./details/Charges.vue";
import Claim from "./details/Claim.vue";
import Timeline from "./details/Timeline.vue";

const service = SalesService.build();

export default {
  page: {
    title: "Vendas",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Actions,
    SaleParticipations,
    Client,
    Resume,
    Timeline,
    Charges,
    Financial,
    Claim,
  },
  data() {
    return {
      sale: {},
    };
  },
  computed: {
    seller() {
      const parts = (this.sale && this.sale.saleParticipations) || [];
      return parts.find((p) => p.role === "SELLER") || { balance: 0 };
    },
    saleParticipations() {
      if (!this.sale || !this.sale.saleParticipations) return [];

      const priority = {
        SELLER: 1,
        CO_SELLER: 2,
        AFFILIATE: 3,
      };

      const ordered = [...this.sale.saleParticipations].sort((a, b) => {
        const pa = priority[a.role] || Number.MAX_SAFE_INTEGER;
        const pb = priority[b.role] || Number.MAX_SAFE_INTEGER;
        return pa - pb;
      });

      return ordered;
    },
    financialData() {
      let totalBalance = 0;

      if (this.sale.saleParticipations) {
        totalBalance = this.sale.saleParticipations.reduce((sum, item) => {
          return sum + (item.balance || 0);
        }, 0);
      }

      const data = {
        total_value: this.sale.total || 0,
        net_value: totalBalance,
        seller_value: this.seller.balance || 0,
      };

      return data;
    },
  },
  watch: {
    '$route.params.id'(newId, oldId) {
      if (newId !== oldId) {
        this.getDetails();
      }
    },
  },
  methods: {
    async getDetails() {
      const requestedRouteId = String(this.$route.params.id);
      const requestedId = encodeURIComponent(requestedRouteId);

      this.sale = {};

      try {
        const res = await service.search({ url: `/${requestedId}` });

        const sale = (res && res.messages && res.messages.sale) || {};

        if (Array.isArray(sale.saleParticipations)) {
          sale.saleParticipations = sale.saleParticipations.map((participation) => ({
            ...participation,
            id: participation.id != null ? participation.id : participation.role,
          }));
        }

        if (requestedRouteId  !== String(this.$route.params.id)) return;

        this.sale = sale;
      } catch (_) {
        if (requestedRouteId  !== String(this.$route.params.id)) return;

        this.sale = {};

        this.$bvToast.toast("Não foi possível buscar os detalhes da venda.", {
          title: "Ops, algum erro aconteceu",
          variant: "danger",
          solid: true,
          autoHideDelay: 2000,
        });
      }
    },
  },
  mounted() {
    this.getDetails();
  },
};
</script>

<template>
  <div class="sales-details">
    <div class="row">
      <div class="col-12">
        <div class="card-box d-flex justify-content-between px-3">
          <div class="">
            <router-link to="/vendas-next" class="sales-details__back-link">
              <i class="mdi mdi-chevron-left mr-1"></i>
              <span>Voltar</span>
            </router-link>
            <h4 id="title" class="sales-details__title">Detalhes da Venda</h4>
          </div>
          <Actions :sale="sale" @reload="getDetails"/>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="row col-md-12 col-lg-8 m-0 p-0">
        <div class="col-12">
          <Resume :sale="sale" />
        </div>
        <div class="col-12 row m-0 p-0">
          <div class="col-6">
            <Client :client="sale.client ? { ...sale.client, total: sale.total } : {}" />
          </div>
          <div class="col-6" :key="participation.id" v-for="participation in saleParticipations">
            <SaleParticipations :seller="participation" />
          </div>
        </div>
        <div
          v-if="sale.type === 'SUBSCRIPTION' || sale.type === 'CONTRACT'"
          class="col-12"
        >
          <Charges :sale="sale" @reload="getDetails" />
        </div>
        <div class="col-12">
          <Financial :financial="financialData" :fees="sale.fees || []" />
        </div>
      </div>
      <div class="sales-details__right-column row col-md-12 col-lg-4">
        <div class="col-12">
          <Claim v-if="sale.claim" :claim="sale.claim" />
        </div>
        <div class="col-12">
          <Timeline :sale-id="$route.params.id" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.sales-details {
  padding-top: 25px;

  &__back-link {
    display: flex;
    align-items: center;

    i {
      font-size: 20px;
      color: #999e9d;
    }

    span {
      color: #666d6d;
    }
  }

  &__right-column {
    display: flex;
    flex-direction: row;
    margin: 0;
    padding: 0;
    height: fit-content;
  }

  &__bar {
    border-top: 1px solid #e5e7e7;
    margin-bottom: 1.5rem;
  }

  &__title {
    font-weight: 500;
    font-size: 24px;
    line-height: 36px;
    margin: 0;
    color: #000c0b;
  }

  &__subtitle {
    font-weight: 600;
    font-size: 16px;
    line-height: 16px;
    color: #000c0b;
    gap: 0.5rem;
    display: flex;
    align-items: center;
    margin-bottom: 2rem;

    img {
      height: 15px !important;
    }
  }

  p {
    margin-bottom: 0;
  }

  &__table {
    border: none !important;

    thead th {
      border: 0;
      border-bottom: 1px solid #e5e7e7;
    }

    th {
      color: #000c0b;
      font-weight: 600;
      padding-left: 0px;
      padding-right: 0px;
    }

    tbody tr {
      border-bottom: 1px solid #e5e7e7;
    }

    tr:first-child {
      border-top: none;

      td {
        padding-top: 0px;
        padding-bottom: 0px;
      }
    }

    td {
      border: none !important;
      padding-left: 0px;
      padding-right: 0px;
      vertical-align: middle;
      height: 50px;
      padding: 10px 0px;
    }
  }

  &__button {
    justify-content: center;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--Gray-500, #000c0b);
    border-color: #f0f2f5 !important;
    border-radius: 8px;
    padding: 14px;
    box-shadow: none !important;
    background-color: var(--Gray-10, #f0f2f5) !important;
    width: fit-content;
    min-width: 44px;
    height: 44px;

    &:hover {
      color: #000c0b;
    }
  }
}
</style>
